// src/components/BOQCoveragePanel.tsx
// G11 / R09 — BOQ-to-PO coverage panel (Sprint 3)
// Embedded in New PO form. Click a NOT_ORDERED row to pre-fill description.

import { MOCK_JOBS, MOCK_POS } from '../utils/mockData'

export type BOQLineStatus = 'COVERED' | 'PARTIAL' | 'NOT_ORDERED'

interface BOQRow {
  id: string; code: string; description: string
  boqQty: number; unit: string; poNumber: string | null; status: BOQLineStatus
}

interface BOQCoveragePanelProps {
  jobId: string
  onPrefill?: (description: string, qty: number, unit: string) => void
}

const STATUS_STYLE: Record<BOQLineStatus, { bg: string; text: string; border: string; label: string }> = {
  COVERED:     { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Covered' },
  PARTIAL:     { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'On order' },
  NOT_ORDERED: { bg: 'bg-red-50',   text: 'text-red-700',   border: 'border-red-200',   label: 'Not ordered' },
}

export function BOQCoveragePanel({ jobId, onPrefill }: BOQCoveragePanelProps) {
  const job = MOCK_JOBS.find(j => j.id === jobId)
  if (!job || !(job as any).bomLines || (job as any).bomLines.length === 0) return null

  const rows: BOQRow[] = ((job as any).bomLines as any[])
    .filter(b => b.materialType === 'BUILD_MATERIAL')
    .map(b => {
      let status: BOQLineStatus = 'NOT_ORDERED'
      if (b.status === 'DELIVERED') status = 'COVERED'
      else if (b.status === 'ON_ORDER' || b.poNumber) status = 'PARTIAL'
      return { id: b.id, code: b.code, description: b.description, boqQty: b.qty, unit: b.unit, poNumber: b.poNumber ?? null, status }
    })

  if (rows.length === 0) return null

  const notOrdered = rows.filter(r => r.status === 'NOT_ORDERED').length
  const partial    = rows.filter(r => r.status === 'PARTIAL').length
  const covered    = rows.filter(r => r.status === 'COVERED').length

  return (
    <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
      <div className="bg-gray-50 px-4 py-2.5 border-b flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-700">BOQ coverage — {job.jobNumber}</span>
          {notOrdered > 0 && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">{notOrdered} not ordered</span>}
          {partial > 0    && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">{partial} on order</span>}
          {covered > 0    && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">{covered} covered</span>}
        </div>
        {onPrefill && notOrdered > 0 && <div className="text-[10px] text-gray-400">Click a red row to pre-fill this PO</div>}
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-gray-50 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
            <th className="text-left px-4 py-2">Description</th>
            <th className="text-right px-3 py-2">BOQ qty</th>
            <th className="text-left px-3 py-2">PO ref</th>
            <th className="text-center px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map(row => {
            const s = STATUS_STYLE[row.status]
            const clickable = row.status === 'NOT_ORDERED' && !!onPrefill
            return (
              <tr key={row.id}
                onClick={() => clickable && onPrefill!(row.description, row.boqQty, row.unit)}
                className={`${s.bg} ${clickable ? 'cursor-pointer hover:brightness-95' : ''} transition-all`}>
                <td className="px-4 py-2">
                  <div className="font-medium text-gray-900">{row.description}</div>
                  <div className="text-[10px] text-gray-400 font-mono">{row.code}</div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600">{row.boqQty} {row.unit}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-gray-400">{row.poNumber ?? '—'}</td>
                <td className="px-4 py-2 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${s.bg} ${s.text} ${s.border}`}>{s.label}</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
